import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { reaction } from "mobx";
import ScriptWaveformDisplay from "./ScriptWaveformDisplay";
import type { ScriptLayerScene, ScriptLayerSegment, TransitionConfig } from "../../../engine/clips/script/script.interfaces";
import { DEFAULT_SCENE_TRANSITION } from "../../../engine/clips/script/script.interfaces";
import Clip from "../../../librettoEditor/modules/timeline/components/Clip/Clip";
import { TransitionHandle, TransitionConfig as UITransitionConfig, VisualTransitionConfig } from "../../../librettoEditor/modules/timeline/components/TransitionHandle";
import { CompositionStateStore } from "../../../librettoEditor/store/CompositionStateStore";
import { ScriptLayerStore } from "../../../librettoEditor/store/ScriptLayerStore";
import { useAssetStore } from "../../../storage/AssetStore";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { ProjectAssetsStore } from "../../../state/ProjectAssetsStore";
import { ApplicationStore } from "../../../librettoEditor/store/ApplicationStore";
import { CompositionEventsEnum } from "../../../librettoEditor/CompositionEvents";
import { WaveformProgressStore } from "../../../state/WaveformProgressStore";

interface ScriptClipProps {
  clipId: string;
  layerId: string;
}

type RawWaveformSource = {
  assetId: string;
  peaks: Float32Array;
  color: string;
};

/**
 * Remap a waveform from the clip's pre-edit time domain into the timeline domain.
 *
 * - Segments are in clip-local time (srcStartSec/srcEndSec) and map to composition time (compStartSec).
 * - Cuts collapse time by making segments pack earlier on the timeline.
 * - Gap insertions create holes by shifting downstream segments later (compStartSec jumps).
 * - Gap segments (isGap: true) are included as silence (zeros).
 */
function remapWaveformToTimeline(
  peaks: Float32Array,
  segments: ScriptLayerSegment[],
  clipDuration: number,
  timelineDuration: number,
  clipStartSec: number,
): Float32Array {
  if (!peaks.length || !segments.length || clipDuration <= 0 || timelineDuration <= 0) {
    return peaks;
  }

  const samplesPerSecond = peaks.length / clipDuration;
  const outputLength = Math.ceil(timelineDuration * samplesPerSecond);
  const output = new Float32Array(outputLength);

	for (const seg of segments) {
	  const segDuration = seg.srcEndSec - seg.srcStartSec;
	  if (segDuration <= 0) continue;

	  const compStartRel = (seg.compStartSec ?? 0) - (clipStartSec ?? 0);
	  if (!Number.isFinite(compStartRel)) continue;
	  let outStartIdx = Math.floor(compStartRel * samplesPerSecond);
	  if (!Number.isFinite(outStartIdx)) continue;
	  if (outStartIdx >= outputLength) continue;

	  let srcStartIdx = Math.max(0, Math.floor((seg.srcStartSec / clipDuration) * peaks.length));
	  const srcEndIdx = Math.min(peaks.length, Math.ceil((seg.srcEndSec / clipDuration) * peaks.length));
	  let segmentSamples = Math.max(0, srcEndIdx - srcStartIdx);

	  // Defensive: if upstream state is temporarily inconsistent (e.g. stale segments vs clipStartSec),
	  // clamp instead of crashing on negative Float32Array.set offsets.
	  if (outStartIdx < 0) {
	    const skip = Math.min(segmentSamples, -outStartIdx);
	    srcStartIdx += skip;
	    segmentSamples = Math.max(0, segmentSamples - skip);
	    outStartIdx = 0;
	  }

	  const copyLen = Math.min(segmentSamples, outputLength - outStartIdx);
	  if (copyLen <= 0) continue;

	  if (!seg.isGap) {
	    output.set(peaks.subarray(srcStartIdx, srcStartIdx + copyLen), outStartIdx);
	  }
	}

  return output;
}

// Default waveform color - dark grey
const WAVEFORM_COLOR = '#374151';

const normalizeState = (value: any) => String(value ?? '').toLowerCase();

const isSilentState = (value: any) => {
  const state = normalizeState(value);
  return state === 'silent' || state === 'not_needed';
};

const isTrackSilent = (track: any): boolean => {
  if (!track) return false;
  if (isSilentState(track?.transcriptionState)) return true;
  if (isSilentState(track?.enhancedTrackState)) return true;
  return false;
};

function processWaveformToClipTimeline(params: {
  fullData: Float32Array;
  sourceDurationSec: number;
  clipDurationSec: number;
  entry: any;
}): Float32Array {
  const { fullData, sourceDurationSec, clipDurationSec, entry } = params;
  if (!fullData || fullData.length === 0) return new Float32Array(0);
  if (!Number.isFinite(sourceDurationSec) || sourceDurationSec <= 0) return fullData;
  if (!Number.isFinite(clipDurationSec) || clipDurationSec <= 0) return fullData;

  const samplesPerSecond = fullData.length / sourceDurationSec;
  if (!Number.isFinite(samplesPerSecond) || samplesPerSecond <= 0) return fullData;

  const totalSamples = Math.max(1, Math.ceil(clipDurationSec * samplesPerSecond));
  const processedPeaks = new Float32Array(totalSamples);

  const entryPlaybackRate = Number(entry?.playbackRate ?? 1.0) || 1.0;

  const entrySegmentsRaw: any[] =
    Array.isArray(entry?.segments) && entry.segments.length > 0 ? entry.segments : [];

  const segments: Array<{
    offsetSec: number;
    rangeInSec: number;
    rangeOutSec: number;
    playbackRate: number;
  }> =
    entrySegmentsRaw.length > 0
      ? entrySegmentsRaw
          .map((seg) => ({
            offsetSec: Number(seg?.offsetSec ?? 0),
            rangeInSec: Number(seg?.range?.inSec ?? 0),
            rangeOutSec:
              seg?.range?.outSec == null ? Number.POSITIVE_INFINITY : Number(seg?.range?.outSec),
            playbackRate: Number(seg?.playbackRate ?? entryPlaybackRate) || 1.0,
          }))
          .filter((s) => Number.isFinite(s.offsetSec) && Number.isFinite(s.rangeInSec) && s.playbackRate > 0)
      : [
          {
            offsetSec: Number(entry?.offsetSec ?? 0),
            rangeInSec: Number(entry?.range?.inSec ?? 0),
            rangeOutSec:
              entry?.range?.outSec == null ? Number.POSITIVE_INFINITY : Number(entry?.range?.outSec),
            playbackRate: entryPlaybackRate,
          },
        ];

  segments.sort((a, b) => a.offsetSec - b.offsetSec);

  for (const seg of segments) {
    const offsetSec = seg.offsetSec;
    const playbackRate = Number(seg.playbackRate) || 1.0;
    if (!Number.isFinite(offsetSec) || !Number.isFinite(playbackRate) || playbackRate <= 0) continue;

    const rangeInSec = Math.max(0, Math.min(sourceDurationSec, Number(seg.rangeInSec) || 0));
    const rawOut = seg.rangeOutSec;
    const rangeOutSec = rawOut === Number.POSITIVE_INFINITY
      ? sourceDurationSec
      : Math.max(rangeInSec, Math.min(sourceDurationSec, Number(rawOut) || sourceDurationSec));
    const sourceRangeDuration = rangeOutSec - rangeInSec;
    if (!(sourceRangeDuration > 0)) continue;

    const startIdx = Math.floor((rangeInSec / sourceDurationSec) * fullData.length);
    const endIdx = Math.ceil((rangeOutSec / sourceDurationSec) * fullData.length);
    const slicedPeaks = fullData.subarray(Math.max(0, startIdx), Math.min(fullData.length, endIdx));
    if (slicedPeaks.length === 0) continue;

    // Timeline duration for this segment (accounts for per-segment playbackRate).
    const segmentTimelineDuration = sourceRangeDuration / playbackRate;
    if (!(segmentTimelineDuration > 0)) continue;

    const offsetSamples = Math.floor(offsetSec * samplesPerSecond);
    if (!Number.isFinite(offsetSamples)) continue;

    const segmentTimelineSamples = Math.max(1, Math.ceil(segmentTimelineDuration * samplesPerSecond));

    // Resample slicedPeaks into the timeline sample count (downsample or upsample).
    let segmentPeaks: Float32Array;
    if (playbackRate !== 1.0 && slicedPeaks.length > 0 && segmentTimelineSamples > 0) {
      segmentPeaks = new Float32Array(segmentTimelineSamples);
      const ratio = slicedPeaks.length / segmentTimelineSamples;
      for (let i = 0; i < segmentTimelineSamples; i++) {
        const srcStart = Math.floor(i * ratio);
        const srcEnd = Math.min(slicedPeaks.length, Math.floor((i + 1) * ratio));

        if (srcEnd <= srcStart) {
          const sample = slicedPeaks[Math.min(slicedPeaks.length - 1, Math.max(0, srcStart))];
          segmentPeaks[i] = sample;
          continue;
        }

        let maxVal = 0;
        for (let j = srcStart; j < srcEnd; j++) {
          if (Math.abs(slicedPeaks[j]) > Math.abs(maxVal)) {
            maxVal = slicedPeaks[j];
          }
        }
        segmentPeaks[i] = maxVal;
      }
    } else {
      segmentPeaks = slicedPeaks;
    }

    const destStart = Math.max(0, offsetSamples);
    if (destStart >= totalSamples) continue;
    const copyLength = Math.min(segmentPeaks.length, totalSamples - destStart);
    if (copyLength <= 0) continue;

    // If segments overlap, preserve the louder amplitude (max abs).
    for (let i = 0; i < copyLength; i++) {
      const prev = processedPeaks[destStart + i];
      const next = segmentPeaks[i];
      processedPeaks[destStart + i] = Math.abs(next) > Math.abs(prev) ? next : prev;
    }
  }

  return processedPeaks;
}

const ScriptClip: React.FC<ScriptClipProps> = observer(({ clipId, layerId }) => {
  const [filename, setFilename] = useState<string | undefined>(CompositionStateStore.getClipTitle(clipId));
  const [thumbnail, setThumbnail] = useState<string | undefined>(undefined);
  const [peaks, setPeaks] = useState<Float32Array | null>(null);
  const [rawMultiSourceWaveforms, setRawMultiSourceWaveforms] = useState<RawWaveformSource[]>([]);
  const { GetWaveformData, GetThumbnailUrl } = useAssetStore();

  // Scene hover state
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredSceneIndex, setHoveredSceneIndex] = useState<number | null>(null);
  const clipContainerRef = useRef<HTMLDivElement>(null);

  // Studio Sound state for waveform selection (original vs denoised)
  const [studioSoundEnabled, setStudioSoundEnabled] = useState(() => {
    const clip: any = CompositionService.getClipById(clipId);
    return Boolean(clip?.studioSoundEnabled);
  });

  // Track selection state from ApplicationStore for scene-level focus
  const isFocused = ApplicationStore.selectedClipId === clipId;
  const selectedSceneIndex = ApplicationStore.selectedSceneIndex;
  const selectedRange = ApplicationStore.selectedScriptLayerRange;

  // Watch for filename changes
  useEffect(() => {
    const disposer = reaction(
      () => ({ filename: CompositionStateStore.getClipTitle(clipId) }),
      ({ filename }) => setFilename(filename),
      { fireImmediately: true }
    );
    return () => disposer();
  }, [clipId]);

  // Listen for studioSoundChanged events from the engine clip
  useEffect(() => {
    const clip = CompositionService.getClipById(clipId) as any;
    if (!clip) return;

    const handleStudioSoundChanged = (event: any) => {
      const data = event?.detail ?? event;
      setStudioSoundEnabled(Boolean(data?.enabled));
      // Clear fetched waveforms so they get re-fetched with new denoised flag
      fetchedRef.current.waveformAssetIds.clear();
      setPeaks(null);
      setRawMultiSourceWaveforms([]);
    };

    const listenerId = clip.on?.('studioSoundChanged', handleStudioSoundChanged);

    // Initialize from current state
    if (clip.studioSoundEnabled !== undefined) {
      setStudioSoundEnabled(Boolean(clip.studioSoundEnabled));
      // Clear any cached waveforms so we fetch the correct variant if the clip was restored.
      fetchedRef.current.waveformAssetIds.clear();
    }

    return () => {
      if (listenerId) clip.off?.(listenerId);
    };
  }, [clipId]);

  // Track what we've already fetched to prevent infinite loops
  const fetchedRef = useRef<{
    thumbnailAssetId?: string;
    waveformAssetIds: Set<string>;
  }>({ waveformAssetIds: new Set() });
  const waveformFetchSeq = useRef(0);

  // Main effect: Handle script clip with multiple audio/video sources
  useEffect(() => {
    if (thumbnail) return;
    const clip: any = CompositionService.getClipById(clipId);
    if (!clip) {
      console.log('[ScriptClip] Clip not found for clipId:', clipId);
      return;
    }

    // Check if this is a ScriptLayerClip
    if (clip.type === 'script' && typeof clip.getVideoSources === 'function') {
      const videoSources = clip.getVideoSources();

      // Fetch thumbnail from first video source if available (only once)
      if (videoSources && videoSources.length > 0) {
        let videoAssetId: string | undefined;
        for (const videoSource of videoSources) {
          const source = videoSource?.source;
          if (source && 'id' in source && source.id) {
            videoAssetId = source.id;
            break;
          }
        }

        if (videoAssetId && fetchedRef.current.thumbnailAssetId !== videoAssetId) {
          fetchedRef.current.thumbnailAssetId = videoAssetId;
          (async () => {
            try {
              const thumbnailUrl = await GetThumbnailUrl({
                thumbnailId: videoAssetId,
                projectId: ProjectAssetsStore.projectId as string
              });
              setThumbnail(thumbnailUrl || undefined);
            } catch (e) {
              console.warn('[ScriptClip] Failed to fetch thumbnail for video source', videoAssetId, e);
            }
          })();
        }
 	      }
    }
  }, [clipId, thumbnail]);

  // Watch for waveform availability for multi-source clips
	  useEffect(() => {
	    const seq = ++waveformFetchSeq.current;

    // Studio Sound changes should switch waveforms; clear cached fetch state + visible peaks first
    // to avoid showing mismatched waveforms during async fetch.
    fetchedRef.current.waveformAssetIds.clear();
    setPeaks(null);
    setRawMultiSourceWaveforms([]);

    const clip: any = CompositionService.getClipById(clipId);
    if (!clip) return;

	    if (clip.type === 'script' && typeof clip.getAudioEntries === 'function') {
	      const audioEntries = clip.getAudioEntries();
	      if (audioEntries && audioEntries.length > 0) {
	        const disposers: Array<() => void> = [];

	        audioEntries.forEach((entry: any, index: number) => {
	          const assetId = entry?.assetId;
	          if (!assetId) return;
	          if (fetchedRef.current.waveformAssetIds.has(assetId)) return;

	          const disposer = reaction(
	            () => {
	              const t = ProjectAssetsStore.projectTracks.find((x: any) => x.id === assetId || x.trackId === assetId);
	              const durationFromStore = CompositionStateStore.clips?.[clipId]?.duration ?? 0;
	              const clipDuration = Number.isFinite(durationFromStore)
	                ? Number(durationFromStore)
	                : (clip.duration?.seconds ?? clip.timelineDuration?.seconds ?? 0);
	              // Check appropriate waveform availability based on Studio Sound state
	              const useDenoised = studioSoundEnabled && !isTrackSilent(t);
	              const hasWaveform = useDenoised
	                ? t?.enhancedTrackState === 'ready'
	                : t?.hasWaveform === true;
	              return { hasWaveform, clipDuration, useDenoised };
	            },
	            async ({ hasWaveform, clipDuration, useDenoised }) => {
	              if (hasWaveform && !fetchedRef.current.waveformAssetIds.has(assetId) && clipDuration > 0) {
	                fetchedRef.current.waveformAssetIds.add(assetId);
	                try {
	                  const fullData = await GetWaveformData({ trackId: assetId, denoised: useDenoised });
	                  if (waveformFetchSeq.current !== seq) return;
	                  if (fullData && fullData.length) {
                    setRawMultiSourceWaveforms(prev => {
                      if (waveformFetchSeq.current !== seq) return prev;
                      const updated = [...prev];
                      while (updated.length <= index) {
                        updated.push({ assetId: "", peaks: new Float32Array(0), color: WAVEFORM_COLOR });
                      }
                      updated[index] = { assetId, peaks: fullData, color: WAVEFORM_COLOR };
                      return updated;
                    });
                  } else {
                    fetchedRef.current.waveformAssetIds.delete(assetId);
                  }
                } catch (e) {
                  console.warn('[ScriptClip] Failed to fetch waveform for source', assetId, e);
                  fetchedRef.current.waveformAssetIds.delete(assetId);
                }
              }
            },
            { fireImmediately: true }
          );
          disposers.push(disposer);
        });

        return () => { disposers.forEach(d => d()); };
      }
    }

    // Single-source clip reaction
    const trackId = CompositionStateStore.clipIdToAssetId?.[clipId];
    if (!trackId) return;
    if (fetchedRef.current.waveformAssetIds.has(trackId)) return;

    const disposer = reaction(
      () => {
        const t = ProjectAssetsStore.projectTracks.find((x: any) => x.id === trackId || x.trackId === trackId);
        const useDenoised = studioSoundEnabled && !isTrackSilent(t);
        return {
          hasWaveform: useDenoised
            ? t?.enhancedTrackState === 'ready'
            : t?.hasWaveform === true,
          useDenoised,
        };
      },
      async ({ hasWaveform, useDenoised }) => {
        if (hasWaveform && !fetchedRef.current.waveformAssetIds.has(trackId)) {
          fetchedRef.current.waveformAssetIds.add(trackId);
          try {
            const data = await GetWaveformData({ trackId, denoised: useDenoised });
            if (waveformFetchSeq.current !== seq) return;
            if (data && data.length) {
              setPeaks(data);
            } else {
              fetchedRef.current.waveformAssetIds.delete(trackId);
            }
          } catch {
            fetchedRef.current.waveformAssetIds.delete(trackId);
          }
        }
      },
      { fireImmediately: true }
    );
    return () => { disposer(); };
  }, [clipId, studioSoundEnabled]);

  // Single-source waveform peaks (pre-edit domain)
  const primaryTrackId = CompositionStateStore.clipIdToAssetId?.[clipId];
  const primaryWaveformProgress = WaveformProgressStore.get(primaryTrackId);
  const displayPeaksVersion = primaryWaveformProgress?.version ?? 0;
  const displayPeaks =
    peaks && peaks.length
      ? peaks
      : primaryWaveformProgress?.peaks && primaryWaveformProgress.peaks.length
        ? primaryWaveformProgress.peaks
        : null;

  const duration = CompositionStateStore.clips?.[clipId]?.trimmedDuration ?? 0;

  // Effective duration for waveform display (timeline duration)
  // Note: CompositionStateStore.trimmedDuration is already timeline duration
  // (set from clip.timelineDuration which accounts for playbackRate)
  const effectiveDuration = duration;

  // Clip duration in the pre-edit "script spine" domain (before cuts collapse time).
  const clipDurationForWaveform =
    CompositionStateStore.clips?.[clipId]?.duration ??
    (CompositionService.getClipById(clipId) as any)?.duration?.seconds ??
    0;

  // Remap raw per-source waveforms (asset domain) into the script-clip timeline domain.
  // This respects per-source multi-segment placement (empty scenes / sequential sources).
  const processedMultiSourceWaveforms = useMemo(() => {
    const engineClip: any = CompositionService.getClipById(clipId);
    if (!engineClip || String(engineClip.type) !== 'script' || typeof engineClip.getAudioEntries !== 'function') {
      return [];
    }
    const audioEntries: any[] = engineClip.getAudioEntries() ?? [];
    if (!audioEntries.length) return [];

    const clipDuration = Number(clipDurationForWaveform ?? 0);
    if (!Number.isFinite(clipDuration) || clipDuration <= 0) return [];

    const byAssetId = new Map<string, Float32Array>();
    for (const w of rawMultiSourceWaveforms) {
      if (w?.assetId && w.peaks && w.peaks.length) {
        byAssetId.set(w.assetId, w.peaks);
      }
    }
    // While local waveform/LUFS compute is still running, we can show partial peaks
    // from the in-memory progress store (fills in progressively over time).
    for (const entry of audioEntries) {
      const assetId = entry?.assetId;
      if (!assetId) continue;
      if (byAssetId.has(assetId)) continue;
      const progress = WaveformProgressStore.get(assetId);
      if (progress?.peaks && progress.peaks.length) {
        byAssetId.set(assetId, progress.peaks);
      }
    }

    const out: Array<{ peaks: Float32Array; color: string }> = [];
    for (let sourceIndex = 0; sourceIndex < audioEntries.length; sourceIndex++) {
      const entry = audioEntries[sourceIndex];
      const assetId = entry?.assetId;
      const fullData = assetId ? byAssetId.get(assetId) : undefined;
      if (!assetId || !fullData || fullData.length === 0) {
        out[sourceIndex] = { peaks: new Float32Array(0), color: WAVEFORM_COLOR };
        continue;
      }

      const trackRecord = ProjectAssetsStore.projectTracks.find(
        (t: any) => t.id === assetId || t.trackId === assetId
      );
      const sourceDurationSec = Number(trackRecord?.duration ?? 0);

      out[sourceIndex] = {
        peaks: processWaveformToClipTimeline({
          fullData,
          sourceDurationSec,
          clipDurationSec: clipDuration,
          entry,
        }),
        color: WAVEFORM_COLOR,
      };
    }
    return out;
  }, [
    clipId,
    rawMultiSourceWaveforms,
    clipDurationForWaveform,
    ScriptLayerStore.cutsVersion,
    ScriptLayerStore.scenesVersion,
    ProjectAssetsStore.projectTracks,
    WaveformProgressStore.version,
  ]);

  // Timeline sections for hover UI. This is broader than engine "scenes":
  // - Splits (scene boundaries AND non-scene section markers) create sections
  // - Gap insertions create "gap sections" (holes) that should appear as their own section
  const contentSceneBoundaries = useMemo(() => {
    if (effectiveDuration <= 0) return [];

    const clip: any = CompositionService.getClipById(clipId);
    if (!clip || String(clip.type) !== 'script') {
      return [{ start: 0, end: effectiveDuration }];
    }

    const inSec = Number(clip.range?.[0]?.seconds ?? 0);
    const outSec = Number(clip.range?.[1]?.seconds ?? clip.duration?.seconds ?? inSec);
    const EPS = 1e-3;

    const splitTimes = (clip.splits ?? [])
      .map((s: any) => Number(s.timeSec))
      .filter((t: any) => Number.isFinite(t) && t > inSec + EPS && t < outSec - EPS)
      .sort((a: number, b: number) => a - b);

    const points: number[] = [0];
    for (const t of splitTimes) {
      const mapped = ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(t);
      if (mapped == null) continue;
      if (!Number.isFinite(mapped)) continue;
      if (mapped <= EPS || mapped >= effectiveDuration - EPS) continue;
      points.push(mapped);
    }
    points.push(effectiveDuration);

    points.sort((a, b) => a - b);
    const unique: number[] = [];
    for (const p of points) {
      if (!unique.length || Math.abs(p - unique[unique.length - 1]) > EPS) unique.push(p);
    }

    return unique.slice(0, -1).map((start, i) => ({
      start,
      end: unique[i + 1],
    }));
  }, [clipId, effectiveDuration, ScriptLayerStore.cutsVersion, ScriptLayerStore.scenesVersion, ScriptLayerStore.playbackRate]);

  // Engine scenes (split-derived) for per-scene gain visualization in the waveform.
  const scenes = useMemo((): ScriptLayerScene[] => {
    const clip: any = CompositionService.getClipById(clipId);
    if (!clip || typeof clip.getScenes !== 'function') return [];
    return clip.getScenes();
  }, [clipId, ScriptLayerStore.cutsVersion, ScriptLayerStore.scenesVersion, effectiveDuration]);

  const gapInsertionSections = useMemo(() => {
    const clip: any = CompositionService.getClipById(clipId);
    if (!clip || String(clip.type) !== 'script') return [];
    if (effectiveDuration <= 0) return [];

    const insertions: any[] = Array.isArray(clip.gapInsertions) ? clip.gapInsertions : [];
    if (insertions.length === 0) return [];

    const rate = ScriptLayerStore.playbackRate || 1;
    const outSec = Number(clip.range?.[1]?.seconds ?? clip.duration?.seconds ?? 0);
    const clipStartSec = Number(clip.start?.seconds ?? 0);
    const segs: any[] = Array.isArray(clip.scriptSegments) ? clip.scriptSegments : [];
    const EPS = 1e-3;

    const out: Array<{ start: number; end: number }> = [];

    for (const ins of insertions) {
      const at = Number(ins?.atSec ?? 0);
      const dur = Number(ins?.durationSec ?? 0);
      if (!Number.isFinite(at) || !Number.isFinite(dur) || dur <= EPS) continue;

      const durTimeline = dur / rate;

      // Trailing insertion at clip end: it extends duration but doesn't shift any segment.
      if (Math.abs(at - outSec) <= EPS) {
        const end = effectiveDuration;
        const start = Math.max(0, end - durTimeline);
        if (end - start > EPS) out.push({ start, end });
        continue;
      }

      // Find the first segment that starts at the insertion point (content AFTER the hole).
      const seg = segs.find((s: any) => Math.abs(Number(s?.srcStartSec ?? NaN) - at) <= EPS);
      if (!seg || !Number.isFinite(seg.compStartSec)) continue;

      const end = (Number(seg.compStartSec) - clipStartSec) / rate;
      const start = Math.max(0, end - durTimeline);
      if (end - start > EPS) out.push({ start, end });
    }

    out.sort((a, b) => a.start - b.start);
    return out;
  }, [clipId, effectiveDuration, ScriptLayerStore.cutsVersion, ScriptLayerStore.playbackRate]);

  const hoverSections = useMemo(() => {
    if (effectiveDuration <= 0) return [];

    const points: number[] = [0, effectiveDuration];
    for (const s of contentSceneBoundaries) {
      points.push(s.start, s.end);
    }
    for (const g of gapInsertionSections) {
      points.push(g.start, g.end);
    }

    const EPS = 1e-3;
    points.sort((a, b) => a - b);
    const unique: number[] = [];
    for (const p of points) {
      if (!Number.isFinite(p)) continue;
      const clamped = Math.max(0, Math.min(effectiveDuration, p));
      if (!unique.length || Math.abs(clamped - unique[unique.length - 1]) > EPS) unique.push(clamped);
    }

    const sections: Array<{ start: number; end: number; isInsertedGap: boolean }> = [];
    for (let i = 0; i < unique.length - 1; i++) {
      const start = unique[i];
      const end = unique[i + 1];
      if (end - start <= EPS) continue;
      const isInsertedGap = gapInsertionSections.some((g) => Math.abs(g.start - start) <= EPS && Math.abs(g.end - end) <= EPS);
      sections.push({ start, end, isInsertedGap });
    }

    return sections;
  }, [contentSceneBoundaries, gapInsertionSections, effectiveDuration]);

  // Mouse handlers for scene hover
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!clipContainerRef.current || !effectiveDuration || hoverSections.length <= 1) {
      setHoveredSceneIndex(null);
      return;
    }

    const rect = clipContainerRef.current.getBoundingClientRect();
    const relativeX = e.clientX - rect.left;
    const percentage = relativeX / rect.width;
    const timeAtMouse = percentage * effectiveDuration;

    for (let i = 0; i < hoverSections.length; i++) {
      const section = hoverSections[i];
      if (timeAtMouse >= section.start && timeAtMouse < section.end) {
        setHoveredSceneIndex(i);
        return;
      }
    }
    if (timeAtMouse >= effectiveDuration && hoverSections.length > 0) {
      setHoveredSceneIndex(hoverSections.length - 1);
    }
  };

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => {
    setIsHovered(false);
    setHoveredSceneIndex(null);
  };

  // Scene boundary transition data (for TransitionHandle components)
  // Each boundary has TWO handles: out-transition (previous scene) and in-transition (next scene)
  const sceneBoundaryTransitions = useMemo(() => {
    const engineClip: any = CompositionService.getClipById(clipId);
    if (!engineClip || effectiveDuration <= 0) return [];

    const inSec = Number(engineClip.range?.[0]?.seconds ?? 0);
    const outSec = Number(engineClip.range?.[1]?.seconds ?? engineClip.duration?.seconds ?? inSec);
    const EPS = 1e-3;

    const splitTimes = (engineClip.splits ?? [])
      .filter((s: any) => s?.isSceneBoundary === true)
      .map((s: any) => Number(s.timeSec))
      .filter((t: any) => Number.isFinite(t) && t > inSec + EPS && t < outSec - EPS)
      .sort((a: number, b: number) => a - b);

    const boundaries: Array<{ boundarySceneIndex: number; prevSceneIndex: number; position: number; outTransition: TransitionConfig; inTransition: TransitionConfig }> = [];

    for (let i = 0; i < splitTimes.length; i++) {
      const t = splitTimes[i];
      const timelineTime = ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(t);
      if (timelineTime == null || !Number.isFinite(timelineTime)) continue;
      if (timelineTime <= EPS || timelineTime >= effectiveDuration - EPS) continue;

      const boundarySceneIndex = i + 1;
      const prevSceneIndex = i;
      const position = timelineTime / effectiveDuration;

      boundaries.push({
        boundarySceneIndex,
        prevSceneIndex,
        position,
        outTransition: engineClip.getSceneOutTransition?.(prevSceneIndex) ?? DEFAULT_SCENE_TRANSITION,
        inTransition: engineClip.getSceneInTransition?.(boundarySceneIndex) ?? DEFAULT_SCENE_TRANSITION,
      });
    }

    return boundaries;
  }, [clipId, effectiveDuration, ScriptLayerStore.cutsVersion, ScriptLayerStore.scenesVersion, ScriptLayerStore.playbackRate]);

  // Non-scene split markers ("section points") should show a separator on hover,
  // but MUST NOT create scenes on the scene bar.
  const nonSceneSplitBoundaries = useMemo((): number[] => {
    const engineClip: any = CompositionService.getClipById(clipId);
    if (!engineClip || effectiveDuration <= 0) return [];

    const inSec = Number(engineClip.range?.[0]?.seconds ?? 0);
    const outSec = Number(engineClip.range?.[1]?.seconds ?? engineClip.duration?.seconds ?? inSec);
    const EPS = 1e-3;

    const splitTimes = (engineClip.splits ?? [])
      .filter((s: any) => s?.isSceneBoundary !== true)
      .map((s: any) => Number(s.timeSec))
      .filter((t: any) => Number.isFinite(t) && t > inSec + EPS && t < outSec - EPS)
      .sort((a: number, b: number) => a - b);

    const times: number[] = [];
    for (const t of splitTimes) {
      const timelineTime = ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(t);
      if (timelineTime == null || !Number.isFinite(timelineTime)) continue;
      if (timelineTime <= EPS || timelineTime >= effectiveDuration - EPS) continue;
      times.push(timelineTime);
    }

    times.sort((a, b) => a - b);
    const unique: number[] = [];
    for (const p of times) {
      if (!unique.length || Math.abs(p - unique[unique.length - 1]) > EPS) unique.push(p);
    }
    return unique;
  }, [clipId, effectiveDuration, ScriptLayerStore.cutsVersion, ScriptLayerStore.scenesVersion, ScriptLayerStore.playbackRate]);

  const hoveredContentSceneIndex = useMemo(() => {
    if (hoveredSceneIndex == null || hoveredSceneIndex < 0) return null;
    const section = hoverSections[hoveredSceneIndex];
    if (!section) return null;
    if (section.isInsertedGap) return null;

    const mid = (section.start + section.end) / 2;
    const src = ScriptLayerStore.mapTimelineTimeToSourceTime(mid);
    if (src == null) return null;

    try {
      const clip: any = CompositionService.getClipById(clipId);
      const inSec = Number(clip?.range?.[0]?.seconds ?? 0);
      const outSec = Number(clip?.range?.[1]?.seconds ?? clip?.duration?.seconds ?? inSec);
      if (!Number.isFinite(inSec) || !Number.isFinite(outSec) || src < inSec) return 0;

      const EPS = 1e-3;
      const splitTimes = (clip?.splits ?? [])
        .filter((s: any) => s?.isSceneBoundary === true)
        .map((s: any) => Number(s.timeSec))
        .filter((t: any) => Number.isFinite(t) && t > inSec + EPS && t < outSec - EPS)
        .sort((a: number, b: number) => a - b);

      let idx = 0;
      for (const t of splitTimes) {
        if (src >= t - EPS) idx++;
        else break;
      }
      return idx;
    } catch {
      return null;
    }
  }, [clipId, hoveredSceneIndex, hoverSections, ScriptLayerStore.cutsVersion, ScriptLayerStore.scenesVersion]);

  // Handle out-transition config change (how a scene ends)
  const handleOutTransitionChange = useCallback((
    sceneIndex: number,
    audioConfig?: UITransitionConfig,
    visualConfig?: VisualTransitionConfig
  ) => {
    const engineClip: any = CompositionService.getClipById(clipId);
    if (!engineClip || !engineClip.setSceneOutTransition) return;

    const currentTransition = engineClip.getSceneOutTransition?.(sceneIndex) ?? DEFAULT_SCENE_TRANSITION;

    const newTransition: TransitionConfig = {
      audio: audioConfig ? {
        type: audioConfig.type as any,
        durationMs: audioConfig.durationSec * 1000,
      } : currentTransition.audio,
      video: visualConfig ? {
        // UI uses 'crossfade' where engine uses 'dissolve'
        type: (
          visualConfig.type === 'crossfade'
            ? 'dissolve'
            : visualConfig.type === 'none'
              ? 'cut'
              : visualConfig.type === 'blur'
                ? 'dissolve'
                : visualConfig.type
        ) as any,
        durationMs: (visualConfig.type === 'none' ? 0 : visualConfig.durationSec * 1000),
      } : currentTransition.video,
    };

    engineClip.setSceneOutTransition(sceneIndex, newTransition);

    // For crossfade, also set the in-transition of the next scene
    if (audioConfig?.type === 'crossfade') {
      engineClip.setSceneInTransition?.(sceneIndex + 1, newTransition);
    }

    // Persist sceneProperties changes (transitions) and refresh schedule.
    try { engineClip.track?.composition?.onClipTimingChanged?.(); } catch {}
    try { engineClip.track?.composition?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip: engineClip }); } catch {}

    ScriptLayerStore.bumpCutsVersion();
  }, [clipId]);

  // Handle in-transition config change (how a scene begins)
  const handleInTransitionChange = useCallback((
    sceneIndex: number,
    audioConfig?: UITransitionConfig,
    visualConfig?: VisualTransitionConfig
  ) => {
    const engineClip: any = CompositionService.getClipById(clipId);
    if (!engineClip || !engineClip.setSceneInTransition) return;

    const currentTransition = engineClip.getSceneInTransition?.(sceneIndex) ?? DEFAULT_SCENE_TRANSITION;

    const newTransition: TransitionConfig = {
      audio: audioConfig ? {
        type: audioConfig.type as any,
        durationMs: audioConfig.durationSec * 1000,
      } : currentTransition.audio,
      video: visualConfig ? {
        // UI uses 'crossfade' where engine uses 'dissolve'
        type: (
          visualConfig.type === 'crossfade'
            ? 'dissolve'
            : visualConfig.type === 'none'
              ? 'cut'
              : visualConfig.type === 'blur'
                ? 'dissolve'
                : visualConfig.type
        ) as any,
        durationMs: (visualConfig.type === 'none' ? 0 : visualConfig.durationSec * 1000),
      } : currentTransition.video,
    };

    engineClip.setSceneInTransition(sceneIndex, newTransition);

    // For crossfade, also set the out-transition of the previous scene
    if (audioConfig?.type === 'crossfade' && sceneIndex > 0) {
      engineClip.setSceneOutTransition?.(sceneIndex - 1, newTransition);
    }

    // Persist sceneProperties changes (transitions) and refresh schedule.
    try { engineClip.track?.composition?.onClipTimingChanged?.(); } catch {}
    try { engineClip.track?.composition?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip: engineClip }); } catch {}

    ScriptLayerStore.bumpCutsVersion();
  }, [clipId]);

  // Convert engine TransitionConfig to UI TransitionConfig
  const toUIAudioConfig = (transition?: TransitionConfig): UITransitionConfig => ({
    type: (transition?.audio?.type === 'crossfade' ? 'crossfade' : 'fade') as 'fade' | 'crossfade',
    durationSec: (transition?.audio?.durationMs ?? 0) / 1000,
  });

  const toUIVisualConfig = (transition?: TransitionConfig): VisualTransitionConfig => {
    const engineType = transition?.video?.type;
    const type: VisualTransitionConfig['type'] =
      engineType === 'dissolve'
        ? 'crossfade'
        : engineType === 'fade'
          ? 'fade'
          : engineType === 'wipe'
            ? 'wipe'
            : 'none';
    return {
      type,
      durationSec: (transition?.video?.durationMs ?? 0) / 1000,
    };
  };

  // Determine if we have video sources (for thumbnail display)
  const clip: any = CompositionService.getClipById(clipId);
  const hasVideo = clip?.type === 'script' && typeof clip.getVideoSources === 'function'
    ? (clip.getVideoSources()?.length ?? 0) > 0
    : false;

  // Get clip gain for visual scaling from observable store (default to 1.0)
  // This is observable via MobX, so changes trigger re-render
  const clipGain = ScriptLayerStore.getClipGain(clipId);

  // Observe cuts and segments from ScriptLayerStore
  const segments = ScriptLayerStore.segments;
  const _cutsVersion = ScriptLayerStore.cutsVersion;

  // Get the original clip duration (before cuts) for waveform collapsing
  const originalDuration = clip?.duration?.seconds ?? 0;
  const clipStartSec = clip?.start?.seconds ?? 0;

  // Collapse waveforms to only show playable segments
  const collapsedMultiSourceWaveforms = useMemo(() => {
    if (!segments.length || !processedMultiSourceWaveforms.length || originalDuration <= 0 || effectiveDuration <= 0) {
      return processedMultiSourceWaveforms;
    }
    return processedMultiSourceWaveforms.map(source => ({
      ...source,
      peaks: remapWaveformToTimeline(source.peaks, segments, originalDuration, effectiveDuration, clipStartSec),
    }));
  }, [processedMultiSourceWaveforms, segments, originalDuration, effectiveDuration, clipStartSec, _cutsVersion]);

  // Collapse single-source waveform
  const collapsedDisplayPeaks = useMemo(() => {
    if (!segments.length || !displayPeaks || originalDuration <= 0 || effectiveDuration <= 0) {
      return displayPeaks;
    }
    return remapWaveformToTimeline(displayPeaks, segments, originalDuration, effectiveDuration, clipStartSec);
  }, [displayPeaks, displayPeaksVersion, segments, originalDuration, effectiveDuration, clipStartSec, _cutsVersion]);

  // Merge multi-source waveforms into a single waveform for display
  const mergedPeaks = useMemo(() => {
    if (collapsedMultiSourceWaveforms.length === 0) {
      return collapsedDisplayPeaks;
    }

    const maxLength = Math.max(...collapsedMultiSourceWaveforms.map(s => s.peaks?.length ?? 0));
    if (maxLength === 0) return null;

    const merged = new Float32Array(maxLength);
    for (let i = 0; i < maxLength; i++) {
      let maxVal = 0;
      for (const source of collapsedMultiSourceWaveforms) {
        if (source.peaks && i < source.peaks.length) {
          if (Math.abs(source.peaks[i]) > Math.abs(maxVal)) {
            maxVal = source.peaks[i];
          }
        }
      }
      merged[i] = maxVal;
    }

    return merged;
  }, [collapsedMultiSourceWaveforms, collapsedDisplayPeaks]);

  // Some video files legitimately have no audio stream, so waveform data will never arrive.
  // In that case, don't show a stuck "Processing..." state forever.
  const shouldExpectWaveform = useMemo(() => {
    const clip: any = CompositionService.getClipById(clipId);
    if (!clip) return true;

    // The engine can often tell us immediately if there's any audio across sources.
    // If it says there's no audio, don't wait on local/remote waveform state.
    if (clip.hasAudioStream === false) return false;
    if (clip.hasAudioStream === true) return true;

    const assetIds = new Set<string>();

    // Prefer explicit audio entries (multi-source ScriptClip).
    if (clip.type === 'script' && typeof clip.getAudioEntries === 'function') {
      for (const entry of clip.getAudioEntries() ?? []) {
        const assetId = entry?.assetId;
        if (assetId) assetIds.add(assetId);
      }
    }

    // Fallback: single-source mapping.
    const mappedTrackId = CompositionStateStore.clipIdToAssetId?.[clipId];
    if (mappedTrackId) assetIds.add(mappedTrackId);

    // Final fallback: video sources (covers video-without-audio where getAudioEntries() can be empty).
    if (assetIds.size === 0 && clip.type === 'script' && typeof clip.getVideoSources === 'function') {
      for (const videoSource of clip.getVideoSources() ?? []) {
        const source = videoSource?.source;
        const id = source && 'id' in source ? (source as any).id : undefined;
        if (id) assetIds.add(id);
      }
    }

    // If we can't resolve the underlying assets yet, be conservative and assume a waveform is expected.
    if (assetIds.size === 0) return true;

    const trackRecords = Array.from(assetIds)
      .map((id) => ProjectAssetsStore.projectTracks.find((t: any) => t.id === id || t.trackId === id))
      .filter(Boolean);

    // Tracks snapshot may not have arrived yet; assume expected until we can confirm otherwise.
    if (trackRecords.length === 0) return true;

    const trackExpectsWaveform = (t: any): boolean => {
      const hasAudioStream = t?.hasAudioStream;
      if (hasAudioStream === false) return false;
      if (hasAudioStream === true) return true;

      const stateLower = String(t?.transcriptionState || '').toLowerCase();
      if (stateLower === 'not_needed' || stateLower === 'silent') return false;
      return true;
    };

    return trackRecords.some(trackExpectsWaveform);
  }, [clipId, ProjectAssetsStore.projectTracks]);

  // For clips with no audio stream, show a flat (all-zero) waveform line instead of "Processing...".
  const silentPeaks = useMemo(() => {
    if (shouldExpectWaveform) return null;
    if (effectiveDuration <= 0) return null;
    const peaksPerSecond = 40; // matches legacy TimelineWaveform behavior
    const length = Math.max(1, Math.ceil(effectiveDuration * peaksPerSecond));
    return new Float32Array(length);
  }, [shouldExpectWaveform, effectiveDuration]);

  // Use merged peaks if available, otherwise single-source peaks, otherwise silent fallback.
  const finalPeaks = mergedPeaks || collapsedDisplayPeaks || silentPeaks;

  // Check if waveform is processing (no peaks yet, but we expect it to arrive).
  const isProcessing = shouldExpectWaveform && !finalPeaks && duration > 0;

		  const selectedHighlightRange = useMemo(() => {
		    if (!isFocused || ApplicationStore.rangeToolEnabled || effectiveDuration <= 0) return null;

		    const clamp = (t: number) => Math.max(0, Math.min(effectiveDuration, t));
		    const EPS = 1e-3;

		    // Prefer explicit selection range (section selection on the ScriptClip timeline, including inserted gaps).
		    if (selectedRange) {
		      const rawStart = clamp(Number(selectedRange.start ?? Number.NaN));
		      const rawEnd = clamp(Number(selectedRange.end ?? Number.NaN));
		      if (!Number.isFinite(rawStart) || !Number.isFinite(rawEnd)) return null;
		      const start = Math.min(rawStart, rawEnd);
		      const end = Math.max(rawStart, rawEnd);
		      if (end - start <= EPS) return null;

		      // Snap ONLY when the selected range already matches a known section boundary pair
		      // (e.g. click-selected section). This keeps transcript-driven selections precise.
		      if (hoverSections.length > 0) {
		        const SNAP_EPS = 1e-2; // 10ms tolerance for cross-domain rounding
		        const matched = hoverSections.find(
		          (s) => Math.abs(s.start - start) <= SNAP_EPS && Math.abs(s.end - end) <= SNAP_EPS
		        );
		        if (matched) return { start: matched.start, end: matched.end };
		      }

		      return { start, end };
		    }

		    // Fallback: if something sets only the scene index, highlight the full scene span.
		    if (selectedSceneIndex !== null && Number.isFinite(selectedSceneIndex)) {
		      const boundaries = sceneBoundaryTransitions;
	      const sceneCount = boundaries.length + 1;
	      const idx = Math.max(0, Math.min(sceneCount - 1, selectedSceneIndex));

	      const leftBoundary = idx === 0
	        ? 0
	        : (boundaries.find((b) => b.boundarySceneIndex === idx)?.position ?? 0) * effectiveDuration;
	      const rightBoundary = idx === sceneCount - 1
	        ? effectiveDuration
	        : (boundaries.find((b) => b.prevSceneIndex === idx)?.position ?? 1) * effectiveDuration;

	      const start = clamp(leftBoundary);
	      const end = clamp(rightBoundary);
	      if (!Number.isFinite(start) || !Number.isFinite(end) || end - start <= EPS) return null;
	      return { start, end };
		    }
		    return null;
		  }, [isFocused, selectedSceneIndex, selectedRange, hoverSections, sceneBoundaryTransitions, effectiveDuration, ApplicationStore.rangeToolEnabled]);

  const hoveredSection = hoveredSceneIndex !== null ? hoverSections[hoveredSceneIndex] : null;
  const hoveredGapEdgeBars = useMemo(() => {
    if (!hoveredSection || effectiveDuration <= 0) return [];

    const EPS = 1e-3;
    const MATCH_EPS = 1e-2; // 10ms tolerance for float/mapping rounding
    const toPct = (t: number) => (t / effectiveDuration) * 100;

    const edges: Array<{ key: string; side: 'left' | 'right'; pct: number; time: number }> = [
      { key: 'left', side: 'left', pct: toPct(hoveredSection.start), time: hoveredSection.start },
      { key: 'right', side: 'right', pct: toPct(hoveredSection.end), time: hoveredSection.end },
    ];

    const isGapBoundary = (t: number) =>
      gapInsertionSections.some((g) => Math.abs(g.start - t) <= MATCH_EPS || Math.abs(g.end - t) <= MATCH_EPS);
    const isSceneBoundary = (t: number) =>
      sceneBoundaryTransitions.some((b) => Math.abs(b.position * effectiveDuration - t) <= MATCH_EPS);
    const isNonSceneSplitBoundary = (t: number) =>
      nonSceneSplitBoundaries.some((p) => Math.abs(p - t) <= MATCH_EPS);

    return edges
      .filter((e) => Number.isFinite(e.time))
      .filter((e) => e.time > EPS && e.time < effectiveDuration - EPS)
      .filter((e) => isGapBoundary(e.time) || isNonSceneSplitBoundary(e.time))
      // Scene boundaries already render their own hover separator + transition handle.
      .filter((e) => !isSceneBoundary(e.time));
  }, [hoveredSection, effectiveDuration, gapInsertionSections, sceneBoundaryTransitions, nonSceneSplitBoundaries]);

  return (
    <Clip
      clipId={clipId}
      layerId={layerId}
      filename={filename}
      thumbnail={hasVideo ? thumbnail : undefined}
      backgroundColor={"255, 255, 255"}
      isScriptLayerClip={true}
    >
      {/* Scene hover detection layer */}
      <div
        ref={clipContainerRef}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 100,
          pointerEvents: ApplicationStore.rangeToolEnabled ? 'none' : 'auto',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
	      >
		        {/* Hover overlay */}
		        {isHovered && (
		          <>
	            {hoverSections.length > 1 && hoveredSceneIndex !== null ? (
	              <div
	                style={{
	                  position: 'absolute',
	                  top: 0,
	                  bottom: 0,
	                  left: `${(hoverSections[hoveredSceneIndex].start / effectiveDuration) * 100}%`,
	                  width: `${((hoverSections[hoveredSceneIndex].end - hoverSections[hoveredSceneIndex].start) / effectiveDuration) * 100}%`,
	                  backgroundColor: hoverSections[hoveredSceneIndex].isInsertedGap ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.08)',
	                  pointerEvents: 'none',
	                  transition: 'left 0.1s ease, width 0.1s ease',
	                }}
	              />
            ) : (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.06)',
                  pointerEvents: 'none',
                }}
              />
            )}
		          </>
		        )}

			        {/* Scene boundary separators + transition dots (hover-only) */}
			        {isHovered && hoveredSceneIndex !== null && sceneBoundaryTransitions.map((boundary) => {
			          const showRightEdge = hoveredContentSceneIndex === boundary.prevSceneIndex;
			          const showLeftEdge = hoveredContentSceneIndex === boundary.boundarySceneIndex;
		          if (!showRightEdge && !showLeftEdge) return null;
		          const pct = boundary.position * 100;

          return (
            <React.Fragment key={`scene-boundary-ui-${boundary.boundarySceneIndex}`}>
              {/* Hovered scene's RIGHT edge (boundary is at the scene's right border) */}
              {showRightEdge && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `calc(${pct}% - 12px)`,
                    width: '12px',
                    backgroundColor: '#aaaaaa',
                    pointerEvents: 'none',
                    zIndex: 15,
                  }}
                >
                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'auto',
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <TransitionHandle
                      side="out"
                      currentConfig={toUIAudioConfig(boundary.outTransition)}
                      hasAdjacentClip={true}
                      onConfigChange={(config) => handleOutTransitionChange(
                        boundary.prevSceneIndex,
                        config,
                        undefined
                      )}
                      showVisual={true}
                      visualConfig={toUIVisualConfig(boundary.outTransition)}
                      onVisualConfigChange={(config) => handleOutTransitionChange(
                        boundary.prevSceneIndex,
                        undefined,
                        config
                      )}
                      offsetPercent={50}
                    />
                  </div>
                </div>
              )}

              {/* Hovered scene's LEFT edge (boundary is at the scene's left border) */}
              {showLeftEdge && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${pct}%`,
                    width: '12px',
                    backgroundColor: '#aaaaaa',
                    pointerEvents: 'none',
                    zIndex: 15,
                  }}
                >
                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'auto',
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <TransitionHandle
                      side="in"
                      currentConfig={toUIAudioConfig(boundary.inTransition)}
                      hasAdjacentClip={true}
                      onConfigChange={(config) => handleInTransitionChange(
                        boundary.boundarySceneIndex,
                        config,
                        undefined
                      )}
                      showVisual={true}
                      visualConfig={toUIVisualConfig(boundary.inTransition)}
                      onVisualConfigChange={(config) => handleInTransitionChange(
                        boundary.boundarySceneIndex,
                        undefined,
                        config
                      )}
                      offsetPercent={50}
                    />
                  </div>
                </div>
              )}
            </React.Fragment>
          );
	        })}

	        {/* Inserted-gap boundary separators (hover-only) */}
	        {isHovered && hoveredGapEdgeBars.map((edge) => (
	          <div
	            key={`gap-edge-${edge.key}-${edge.time.toFixed(3)}`}
	            style={{
	              position: 'absolute',
	              top: 0,
	              bottom: 0,
	              left: edge.side === 'right' ? `calc(${edge.pct}% - 12px)` : `${edge.pct}%`,
	              width: '12px',
	              backgroundColor: '#aaaaaa',
	              pointerEvents: 'none',
	              zIndex: 15,
	            }}
	          />
	        ))}

	        {/* Scene transition fade overlays (triangular shading) */}
	        {sceneBoundaryTransitions.map((boundary) => {
	          const outVideoDurationSec = (boundary.outTransition?.video?.durationMs ?? 0) / 1000;
	          const inVideoDurationSec = (boundary.inTransition?.video?.durationMs ?? 0) / 1000;
          const outAudioDurationSec = (boundary.outTransition?.audio?.durationMs ?? 0) / 1000;
          const inAudioDurationSec = (boundary.inTransition?.audio?.durationMs ?? 0) / 1000;

          if (effectiveDuration <= 0) return null;

          const videoFadeOutWidth = outVideoDurationSec > 0
            ? Math.min(boundary.position, outVideoDurationSec / effectiveDuration)
            : 0;
          const videoFadeInWidth = inVideoDurationSec > 0
            ? Math.min(1 - boundary.position, inVideoDurationSec / effectiveDuration)
            : 0;

          const audioFadeOutWidth = outAudioDurationSec > 0
            ? Math.min(boundary.position, outAudioDurationSec / effectiveDuration)
            : 0;
          const audioFadeInWidth = inAudioDurationSec > 0
            ? Math.min(1 - boundary.position, inAudioDurationSec / effectiveDuration)
            : 0;

          if (
            videoFadeOutWidth <= 0 &&
            videoFadeInWidth <= 0 &&
            audioFadeOutWidth <= 0 &&
            audioFadeInWidth <= 0
          ) return null;

          const splitOut = videoFadeOutWidth > 0 && audioFadeOutWidth > 0;
          const splitIn = videoFadeInWidth > 0 && audioFadeInWidth > 0;

          return (
            <React.Fragment key={`transition-fade-${boundary.boundarySceneIndex}`}>
              {/* VIDEO fade-out overlay (before transition point) */}
              {videoFadeOutWidth > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: splitOut ? '50%' : 0,
                    right: `${(1 - boundary.position) * 100}%`,
                    width: `${videoFadeOutWidth * 100}%`,
                    pointerEvents: 'none',
                    zIndex: 5,
                  }}
                >
                  <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                    <polygon points="0,0 100,0 100,100" fill="rgba(0,0,0,0.18)" />
                    <line x1="100" y1="100" x2="0" y2="0" stroke="rgba(255,255,255,0.55)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                  </svg>
                </div>
              )}

              {/* AUDIO fade-out overlay (before transition point) */}
              {audioFadeOutWidth > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: splitOut ? '50%' : 0,
                    bottom: 0,
                    right: `${(1 - boundary.position) * 100}%`,
                    width: `${audioFadeOutWidth * 100}%`,
                    pointerEvents: 'none',
                    zIndex: 5,
                  }}
                >
                  <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                    <polygon points="0,0 100,0 100,100" fill="rgba(0,0,0,0.30)" />
                    <line x1="100" y1="100" x2="0" y2="0" stroke="rgba(255,255,255,0.70)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                  </svg>
                </div>
              )}

              {/* VIDEO fade-in overlay (after transition point) */}
              {videoFadeInWidth > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: splitIn ? '50%' : 0,
                    left: `${boundary.position * 100}%`,
                    width: `${videoFadeInWidth * 100}%`,
                    pointerEvents: 'none',
                    zIndex: 5,
                  }}
                >
                  <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                    <polygon points="0,0 100,0 0,100" fill="rgba(0,0,0,0.18)" />
                    <line x1="0" y1="100" x2="100" y2="0" stroke="rgba(255,255,255,0.55)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                  </svg>
                </div>
              )}

              {/* AUDIO fade-in overlay (after transition point) */}
              {audioFadeInWidth > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: splitIn ? '50%' : 0,
                    bottom: 0,
                    left: `${boundary.position * 100}%`,
                    width: `${audioFadeInWidth * 100}%`,
                    pointerEvents: 'none',
                    zIndex: 5,
                  }}
                >
                  <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                    <polygon points="0,0 100,0 0,100" fill="rgba(0,0,0,0.30)" />
                    <line x1="0" y1="100" x2="100" y2="0" stroke="rgba(255,255,255,0.70)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                  </svg>
                </div>
              )}
            </React.Fragment>
          );
        })}

	        {/* Selected scene highlight (focused border) */}
		    {/*    {selectedHighlightRange && effectiveDuration > 0 && (*/}
		    {/*      <div*/}
		    {/*        style={{*/}
		    {/*          position: 'absolute',*/}
		    {/*          top: 0,*/}
		    {/*          bottom: 0,*/}
		    {/*          left: `${(selectedHighlightRange.start / effectiveDuration) * 100}%`,*/}
		    {/*          width: `${((selectedHighlightRange.end - selectedHighlightRange.start) / effectiveDuration) * 100}%`,*/}
		    {/*          boxShadow: 'inset 0 0 0 2px rgba(43, 98, 253, 1), 0 0 4px rgba(0, 0, 0, 0.15)',*/}
		    {/*          borderRadius: '4px',*/}
		    {/*          pointerEvents: 'none',*/}
		    {/*          zIndex: 2,*/}
		    {/*          transition: 'left 0.1s ease, width 0.1s ease',*/}
		    {/*        }}*/}
		    {/*      />*/}
		    {/*    )}*/}
      </div>

	      {/* Single unified waveform display */}
	      {finalPeaks && effectiveDuration > 0 && (
	        <div style={{ position: 'absolute', inset: 0, opacity: hasVideo ? 0.85 : 1.0 }}>
	          <ScriptWaveformDisplay
	            clipId={clipId}
	            peaks={finalPeaks}
                peaksRevision={displayPeaksVersion}
	            duration={effectiveDuration}
	            rangeEnabled={ApplicationStore.rangeToolEnabled}
	            selectedRange={selectedRange}
	            waveformColor={WAVEFORM_COLOR}
            gain={clipGain}
            scenes={scenes}
            speakerColorSegments={ScriptLayerStore.speakerWaveformSegments}
            speakerColorSegmentsRevision={ScriptLayerStore.speakerWaveformSegmentsVersion}
          />
        </div>
      )}

      {/* Processing indicator badge when waveform not ready */}
      {isProcessing && (
        <div style={{
          position: 'absolute',
          top: '4px',
          left: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '2px 6px',
          backgroundColor: 'rgba(55, 65, 81, 0.85)',
          borderRadius: '4px',
          zIndex: 10,
        }}>
          <div style={{
            width: '10px',
            height: '10px',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderTopColor: '#fff',
            borderRadius: '50%',
            animation: 'clip-spin 1s linear infinite',
          }} />
          <span style={{
            color: '#fff',
            fontSize: '10px',
            fontWeight: 500,
          }}>Processing...</span>
        </div>
      )}
      <style>{`
        @keyframes clip-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Clip>
  );
});

export default ScriptClip;
